// hooks/useGPSDisplacement.ts - CORRIGIDO PARA IMAGENS
import { useCallback, useEffect, useState } from 'react';
import { ProjectImage } from 'services/videoService';
import {
  calculateAdjustedGPSWithBearing,
  DisplacementResult,
  pixelsToMeters,
  VideoDisplacement
} from 'utils/geoCalculations';

// CONSTANTE GLOBAL - Profundidade padrão
const DEFAULT_DEPTH_METERS = 15;

interface UseGPSDisplacementProps {
  currentImage: ProjectImage | null;
  previousImage: ProjectImage | null;
  videoRef: React.RefObject<HTMLVideoElement> | null; // ✅ Agora pode ser null
  enabled: boolean;
}

interface GPSDisplacementState {
  isActive: boolean;
  videoDisplacement: VideoDisplacement | null;
  displacementResult: DisplacementResult | null;
  mousePosition: { x: number; y: number } | null;
}

export const useGPSDisplacement = ({
  currentImage,
  enabled
}: UseGPSDisplacementProps) => {
  const [state, setState] = useState<GPSDisplacementState>({
    isActive: false,
    videoDisplacement: null,
    displacementResult: null,
    mousePosition: null
  });

  // ✅ NOVO: Hook para processar movimento do mouse via ImageViewer
  useEffect(() => {
    if (!enabled) {
      setState({
        isActive: false,
        videoDisplacement: null,
        displacementResult: null,
        mousePosition: null
      });
      return;
    }

    const handleGPSMouseMove = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { relativeX, relativeY, imageRect } = customEvent.detail;

      if (!currentImage || !currentImage.lat || !currentImage.long) {
        console.warn('⚠️ Imagem sem GPS');
        return;
      }

      console.log('🎯 GPS Mouse Move:', { relativeX, relativeY });

      // Calcular pixel offset do centro
      const centerX = imageRect.width / 2;
      const pixelX = relativeX * imageRect.width - centerX;
      const pixelY = relativeY * imageRect.height;

      console.log('🚨 Calculando displacement:', {
        pixelX,
        pixelY,
        width: imageRect.width,
        height: imageRect.height,
        depth: DEFAULT_DEPTH_METERS
      });

      const videoDisplacement = pixelsToMeters(
        pixelX,
        pixelY,
        imageRect.width,
        imageRect.height,
        DEFAULT_DEPTH_METERS
      );

      console.log('🚨 Displacement result:', videoDisplacement);

      const cameraBearing = currentImage.bearing ?? 0;

      const displacementResult = calculateAdjustedGPSWithBearing(
        {
          lat: currentImage.lat,
          lng: currentImage.long
        },
        cameraBearing,
        videoDisplacement
      );

      console.log('✅ GPS Ajustado:', {
        original: { lat: currentImage.lat, lng: currentImage.long },
        adjusted: displacementResult.adjustedGPS,
        offsets: {
          horizontal: videoDisplacement.x.toFixed(2) + 'm',
          depth: videoDisplacement.y.toFixed(2) + 'm'
        }
      });

      setState({
        isActive: true,
        videoDisplacement,
        displacementResult,
        mousePosition: {
          x: relativeX * imageRect.width,
          y: relativeY * imageRect.height
        }
      });
    };

    const handleGPSMouseLeave = () => {
      console.log('🖱️ Mouse saiu da imagem');
      setState({
        isActive: false,
        videoDisplacement: null,
        displacementResult: null,
        mousePosition: null
      });
    };

    window.addEventListener('gpsMouseMove', handleGPSMouseMove);
    window.addEventListener('gpsMouseLeave', handleGPSMouseLeave);

    console.log('✅ GPS listeners attached');

    return () => {
      window.removeEventListener('gpsMouseMove', handleGPSMouseMove);
      window.removeEventListener('gpsMouseLeave', handleGPSMouseLeave);
      console.log('🗑️ GPS listeners removed');
    };
  }, [enabled, currentImage]);

  // ✅ Handler de movimento do mouse (chamado pelo ImageViewer)
  const handleMouseMove = useCallback(() => {
    // Este método é mantido para compatibilidade
    // mas agora usa eventos customizados
    console.log('[useGPSDisplacement] handleMouseMove (deprecated)');
  }, []);

  // Resetar quando a imagem mudar
  useEffect(() => {
    if (currentImage) {
      console.log('📸 Imagem mudou, resetando displacement');
      setState({
        isActive: false,
        videoDisplacement: null,
        displacementResult: null,
        mousePosition: null
      });
    }
  }, [currentImage?.id]);

  const reset = useCallback(() => {
    console.log('🔄 Resetting GPS displacement');
    setState({
      isActive: false,
      videoDisplacement: null,
      displacementResult: null,
      mousePosition: null
    });
  }, []);

  return {
    isActive: state.isActive,
    videoDisplacement: state.videoDisplacement,
    displacementResult: state.displacementResult,
    mousePosition: state.mousePosition,
    handleMouseMove, // Mantido para compatibilidade
    reset
  };
};
